import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { getUserState } from "actions/userActions";
import PageHeading from "components/PageHeading";
import AddGroup from "pages/dashboards/employerDashboard/dashboard/AddGroup";
import RosterApprovalTable from "pages/dashboards/employerDashboard/dashboard/RosterApprovalTable";
import {
  ANNOUNCED_ICON,
  INVITED_EMPLOYEES,
  LAUNCHED_PLAN,
} from "statics/onboardingSteps";
import Announcement from "pages/dashboards/employerDashboard/dashboard/Announcement";
import AddEmployeeUploadFormToggle from "pages/dashboards/employerDashboard/userManagement/AddEmployeeUploadFormToggle";
import AddEmployeeForm from "pages/dashboards/employerDashboard/userManagement/AddEmployeeForm";
import LaunchPlan from "pages/dashboards/employerDashboard/dashboard/LaunchPlan";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import AddEmployerBankCard from "pages/dashboards/employerDashboard/dashboard/AddEmployerBankCard";
import AddGroupCard from "pages/dashboards/employerDashboard/dashboard/AddGroupCard";
import AcknowledgeRecurringDebitsCard from "pages/dashboards/employerDashboard/dashboard/AcknowledgeRecurringDebitsCard";
import AddUsersCard from "pages/dashboards/employerDashboard/dashboard/AddUsersCard";
import AddDeductionCodeCard from "./AddDeductionCodeCard";
import AddDeductionCode from "./AddDeductionCode";
import {
  hasAcknowledgedRecurringDebits,
  hasAddedBank,
  hasAddedDeductionCode,
  hasAddedGroups,
  hasAnnouncedIcon,
  hasEnabled360PayrollIntegration,
  hasInvitedAUserSelector,
  hasInvitedEmployees,
  hasLaunchedPlan,
  hasStatePlanSelector,
  usesDebitAuthorization,
} from "store/selectors/employer";
import {
  groupCreationHide,
  groupCreationShow,
} from "actions/groupCreationActions";

import {
  getRecurringDebitsAcknowledgment,
  updateProgressiveOnboarding,
} from "actions/employerActions";
import { progressiveOnboardingEvents } from "statics/states";
import queryString from "query-string";
import PayrollConnect from "pages/dashboards/employerDashboard/dashboard/PayrollConnect";
import type { RootState } from "types/state";

const mapStateToProps = (state: RootState) => {
  return {
    hasBank: hasAddedBank(state),
    hasGroup: hasAddedGroups(state),
    hasDeductionCode: hasAddedDeductionCode(state),
    hasStatePlan: hasStatePlanSelector(state),
    hasInvitedAUser: hasInvitedAUserSelector(state),
    hasAnnouncedIcon: hasAnnouncedIcon(state),
    hasInvitedEmployees: hasInvitedEmployees(state),
    hasLaunchedPlan: hasLaunchedPlan(state),
    has360PayrollIntegration: hasEnabled360PayrollIntegration(state),
    hasAcknowledgedRecurringDebits: hasAcknowledgedRecurringDebits(state),
    usesDebitAuthorization: usesDebitAuthorization(state),
    companyId: state.employer.company?.id as string | undefined,
    showGroupCreation: state.groupCreation.groupCreation,
  };
};
const mapDispatchToProps = {
  getUserState,
  groupCreationShow,
  groupCreationHide,
  updateProgressiveOnboarding,
  getRecurringDebitsAcknowledgment,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  initialFetching: boolean;
  showGroupForm: boolean;
  // activeOnboardingStep starts as 1 (number) but one onCancel handler sets it
  // to `false` — preserved as-is (pre-existing quirk).
  activeOnboardingStep: number | boolean;
  showAddUsersCard: boolean;
  // read in _getContent but only ever set via setState
  showRosterTable?: boolean;
  showDeductionCode?: boolean;
}

class ProgressiveOnboardingDashboard extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const queryParams = queryString.parse(window.location.search);
    const shouldShowLaunchPlan =
      queryParams.showLaunchPlan === "true" &&
      this.props.hasInvitedEmployees &&
      this.props.hasDeductionCode &&
      !this.props.hasLaunchedPlan;

    this.state = {
      initialFetching: true,
      showGroupForm: false,
      activeOnboardingStep: 1,
      showAddUsersCard: !shouldShowLaunchPlan,
    };
  }

  componentDidMount() {
    this._fetchRecurringDebitsAcknowledgment();
  }

  componentDidUpdate(prevProps: Props) {
    const startedUsingDebitAuthorization =
      !prevProps.usesDebitAuthorization && this.props.usesDebitAuthorization;
    const switchedCompany = prevProps.companyId !== this.props.companyId;
    if (startedUsingDebitAuthorization || switchedCompany) {
      this._fetchRecurringDebitsAcknowledgment();
    }
  }

  _fetchRecurringDebitsAcknowledgment = () => {
    if (this.props.usesDebitAuthorization) {
      void this.props.getRecurringDebitsAcknowledgment(
        this.props.client as ApolloClient<object>
      );
    }
  };

  _getActiveStep = () => {
    const EMPTY = 1;
    if (
      this.props.hasGroup &&
      this.props.hasDeductionCode &&
      !this.props.hasAnnouncedIcon
    ) {
      return ANNOUNCED_ICON;
    } else if (
      this.props.hasDeductionCode &&
      this.props.hasAnnouncedIcon &&
      !this.props.hasInvitedEmployees
    ) {
      return INVITED_EMPLOYEES;
    } else if (
      this.props.hasInvitedEmployees &&
      this.props.hasDeductionCode &&
      !this.props.hasLaunchedPlan
    ) {
      return LAUNCHED_PLAN;
    } else {
      return EMPTY;
    }
  };

  _getContent = () => {
    if (this.props.showGroupCreation) {
      return <AddGroup onClose={this.props.groupCreationHide} />;
    }

    if (this.state.showRosterTable) {
      return (
        <RosterApprovalTable
          onClose={() => this.setState({ showRosterTable: false })}
        />
      );
    }

    if (this.state.showDeductionCode) {
      return (
        <AddDeductionCode
          onClose={() => this.setState({ showDeductionCode: false })}
          onSuccess={() => this.setState({ showDeductionCode: false })}
        />
      );
    }

    if (!this.state.showAddUsersCard) {
      switch (this._getActiveStep()) {
        case ANNOUNCED_ICON:
          return (
            <Announcement
              // @ts-expect-error inert pre-existing props: Announcement does not
              // declare hasDeductionCode/activeOnboardingStep; preserved unchanged.
              hasDeductionCode={this.props.hasDeductionCode}
              activeOnboardingStep={this._getActiveStep()}
              onCancel={() =>
                this.setState({
                  showAddUsersCard: true,
                })
              }
              onSuccess={() => {
                this.setState({
                  activeOnboardingStep: this._getActiveStep(),
                  showAddUsersCard: true,
                });
              }}
            />
          );

        case INVITED_EMPLOYEES:
          {
            if (this.props.hasStatePlan) {
              return (
                <AddEmployeeUploadFormToggle
                  // @ts-expect-error inert pre-existing props: connected
                  // AddEmployeeUploadFormToggle does not declare these props;
                  // preserved to keep JSX output unchanged.
                  hasDeductionCode={this.props.hasDeductionCode}
                  hasAnnouncedIcon={this.props.hasAnnouncedIcon}
                  activeOnboardingStep={this._getActiveStep()}
                  onCancel={() =>
                    this.setState({
                      showAddUsersCard: true,
                      activeOnboardingStep: false,
                    })
                  }
                  onUploadSuccess={() => {
                    this.setState({
                      activeOnboardingStep: this._getActiveStep(),
                      showAddUsersCard: true,
                    });
                  }}
                />
              );
            } else if (!this.props.hasStatePlan) {
              return (
                <AddEmployeeForm
                  // @ts-expect-error inert pre-existing props: AddEmployeeForm does not declare hasDeductionCode/hasAnnouncedIcon/activeOnboardingStep; the form ignores them — preserved from JS
                  hasDeductionCode={this.props.hasDeductionCode}
                  hasAnnouncedIcon={this.props.hasAnnouncedIcon}
                  activeOnboardingStep={this._getActiveStep()}
                  onCancel={() =>
                    this.setState({
                      showAddUsersCard: true,
                    })
                  }
                  onSuccess={() => {
                    this.setState({
                      activeOnboardingStep: this._getActiveStep(),
                      showAddUsersCard: true,
                    });
                  }}
                />
              );
            }
          }
          return;
        case LAUNCHED_PLAN:
          return (
            <LaunchPlan
              // @ts-expect-error inert pre-existing props: LaunchPlan does not
              // declare hasInvitedEmployees/activeOnboardingStep; preserved unchanged.
              hasInvitedEmployees={
                this.props.hasInvitedEmployees && this.props.hasDeductionCode
              }
              activeOnboardingStep={this._getActiveStep()}
              onCancel={() =>
                this.setState({
                  showAddUsersCard: true,
                })
              }
              onSuccess={() => {
                this.setState({
                  activeOnboardingStep: this._getActiveStep(),
                  showAddUsersCard: true,
                });
              }}
            />
          );
      }
    }

    const acknowledgedRecurringDebitsEarly =
      this.props.hasAcknowledgedRecurringDebits;
    const acknowledgesInPayrollIntegration =
      this.props.hasGroup && !acknowledgedRecurringDebitsEarly;
    const showAcknowledgeRecurringDebitsCard =
      this.props.usesDebitAuthorization && !acknowledgesInPayrollIntegration;
    const awaitingRecurringDebitsAcknowledgment =
      showAcknowledgeRecurringDebitsCard && !acknowledgedRecurringDebitsEarly;
    const isGroupSetupLocked =
      !this.props.hasBank || awaitingRecurringDebitsAcknowledgment;

    return (
      <>
        {this.state.showAddUsersCard && (
          <div className="accordian">
            <div className="accordian-intro">
              <IconHeader variant="cardHeader" headerText="Getting Started" />
              <IconSubheader subheader="Complete these steps..." />
            </div>
            <AddEmployerBankCard
              onSuccess={() => {
                // @ts-expect-error pre-existing bug: updateProgressiveOnboarding
                // requires a 3rd `companyId` arg that is omitted here — preserved as-is.
                void this.props.updateProgressiveOnboarding(
                  this.props.client as ApolloClient<object>,
                  progressiveOnboardingEvents.AddBank
                );
              }}
            />
            {showAcknowledgeRecurringDebitsCard && (
              <AcknowledgeRecurringDebitsCard hasBank={this.props.hasBank} />
            )}
            <AddGroupCard isLocked={isGroupSetupLocked} />
            {this.props.has360PayrollIntegration && <PayrollConnect />}
            {!this.props.has360PayrollIntegration && (
              <AddDeductionCodeCard
                // @ts-expect-error inert pre-existing prop: AddDeductionCodeCard
                // does not declare hasGroup; preserved to keep JSX unchanged.
                hasGroup={this.props.hasGroup}
                hasInvitedEmployees={this.props.hasInvitedEmployees}
                showDeductionCode={() =>
                  this.setState({ showDeductionCode: true })
                }
              />
            )}
            <AddUsersCard
              // @ts-expect-error inert pre-existing prop: AddUsersCard does not
              // declare hasDeductionCode; preserved to keep JSX unchanged.
              hasDeductionCode={this.props.hasDeductionCode}
              onStepChange={this._setStep}
              activeStepNumber={this._getActiveStep()}
            />
          </div>
        )}
      </>
    );
  };

  // Called with a step constant at each call site, but the argument is
  // ignored (pre-existing) — rest param preserves that exactly.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- preserved: arg is accepted but unused, as in prior JS
  _onStepChange = (...args: unknown[]) => {
    this.setState({
      showAddUsersCard: false,
    });
  };

  _setStep = () => {
    if (
      this.props.hasGroup &&
      this.props.hasDeductionCode &&
      !this.props.hasAnnouncedIcon
    ) {
      this._onStepChange(ANNOUNCED_ICON);
    } else if (
      this.props.hasGroup &&
      this.props.hasDeductionCode &&
      this.props.hasAnnouncedIcon &&
      !this.props.hasInvitedEmployees
    ) {
      this._onStepChange(INVITED_EMPLOYEES);
    } else if (
      this.props.hasInvitedEmployees &&
      this.props.hasDeductionCode &&
      !this.props.hasLaunchedPlan
    ) {
      this._onStepChange(LAUNCHED_PLAN);
    }
  };

  render() {
    const content = this._getContent();

    return (
      <>
        <PageHeading
          title={"Welcome to Icon!"}
          subtext={
            "We’re here to help you get things rolling. Let's link your bank account, setup your plan, and invite additional users."
          }
        />
        <div className="main-content">{content}</div>
      </>
    );
  }
}

export default connector(withApollo<Props>(ProgressiveOnboardingDashboard));
